<template>
  <div
    v-if="showDisclaimer"
    :class="['app-disclaimers', `app-disclaimers--${variant}`]"
  >
    <div class="row justify-content-center">
      <div class="col-12 col-lg-8">
        <div
          v-if="disclaimerPreview"
          class="app-disclaimers__preview"
          v-html="renderedPreview"
        />
        <div
          v-if="isExpanded && disclaimerFull"
          class="app-disclaimers__full"
          v-html="renderedFull"
        />
        <button
          v-if="disclaimerFull && ctaLabel"
          type="button"
          class="app-disclaimers__cta btn btn-link p-0 text-magenta"
          @click="isExpanded = !isExpanded"
        >
          <span class="app-disclaimers__cta-label">{{ ctaLabel }}</span>
          <FontAwesomeIcon
            :icon="isExpanded ? appIcons.faChevronUp : appIcons.faChevronDown"
            class="app-disclaimers__cta-icon"
          />
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'

import { useAppContentStore } from '@/stores/appContent'
import { mapState } from 'pinia'

import useAppIcons from '@/composables/appIcons'
import { renderMarkdownToHtml } from '@/composables/contentful'

import type { DisclaimerContent } from '@/types/DisclaimerContent'

export enum DisclaimerVariant {
  Default = 'default',
  Dark = 'dark'
}

export default defineComponent({
  name: 'AppDisclaimers',
  setup() {
    return {
      ...useAppIcons(),
      renderMarkdownToHtml
    }
  },
  props: {
    variant: {
      type: String as PropType<DisclaimerVariant>,
      required: false,
      default: DisclaimerVariant.Default
    }
  },
  data() {
    return {
      isExpanded: false
    }
  },
  computed: {
    ...mapState(useAppContentStore, ['marketPageContent', 'disclaimerOverride']),
    disclaimerSource(): DisclaimerContent | null | undefined {
      return this.disclaimerOverride ?? this.marketPageContent
    },
    showDisclaimer(): boolean {
      return this.disclaimerSource?.showDisclaimer ?? false
    },
    disclaimerPreview(): string | undefined {
      return this.disclaimerSource?.disclaimerPreview
    },
    disclaimerFull(): string | undefined {
      return this.disclaimerSource?.disclaimerFull
    },
    ctaLabel(): string | undefined {
      if (this.isExpanded) {
        return this.disclaimerSource?.disclaimerCtaLabelCollapse
      } else {
        return this.disclaimerSource?.disclaimerCtaLabelExpand
      }
    },
    renderedPreview(): string {
      if (!this.disclaimerPreview) return ''
      return this.renderMarkdownToHtml(this.disclaimerPreview)
    },
    renderedFull(): string {
      if (!this.disclaimerFull) return ''
      return this.renderMarkdownToHtml(this.disclaimerFull)
    }
  },
  watch: {
    disclaimerSource() {
      this.isExpanded = false
    }
  }
})
</script>

<style scoped lang="scss">
.app-disclaimers {
  font-size: 0.875rem;
  // Gap to the Klarna blurb below: matches `.app-main`'s bottom margin.
  margin-bottom: 1rem;

  @include media-breakpoint-up(md) {
    margin-bottom: 5.5rem;
  }

  &__cta {
    font-size: 0.875rem;
    text-decoration: none;
  }

  &__cta-label {
    text-decoration: underline;
  }

  &__cta-icon {
    margin-left: 0.25rem;
  }

  &--dark {
    color: #ffffff;
  }
}
</style>
